import { z } from "zod";
import { SchedulingAppointmentSourceSchema } from "./demo-scheduling.js";
import { PatientAdministrativeSexSchema } from "./patient-profile.js";
import { PatientIntakeSectionsSchema } from "./patient-questionnaire.js";
import { PublicIntakeQuestionnaireUrlSchema, VisitIntakeLinkSchema } from "./scheduling.js";
/**
 * Walk-in intake on the patient's phone from a desk-issued QR code (PA-5 / EXECDEV-190). Texting
 * the same link waits for PA-7. The desk path:
 *
 * 1. Quick details (WalkInQuickRegistrationSchema): legal first and last name, date of birth,
 *    sex ("Prefer not to say" is `unknown`), the reason for the visit and an optional mobile
 *    number for PA-7.
 * 2. Duplicate check before any chart is made, through PA-6's one chart matcher:
 *    `POST /v1/scheduling/walk-ins/chart-matches` (WalkInChartMatchesRequestSchema), answered
 *    with SchedulingPatientMatchesSchema, basis walk_in. A POST, so no name or date of birth
 *    enters a URL. Picking a match reuses that chart (the existing start-encounter route with
 *    walkInQuickStartEncounterInput); otherwise the existing walk-in route creates the chart and
 *    the visit from walkInQuickCreateInput, whose shape is create_walk_in's fixed strict input.
 * 3. `POST /v1/scheduling/visits/:encounterId/intake-link` (IssueVisitIntakeLinkRequestSchema)
 *    issues the questionnaire link for a walk-in, or for a checked-in booking whose
 *    questionnaire is not complete, and returns it once (VisitIntakeLinkResponseSchema). The
 *    first link a walk-in gets creates its scheduling row (source walk_in); later ones reuse it.
 *
 * The schedule read then carries the walk-in (SchedulingStateSchema.walkIns) or the booking's
 * link (SchedulingAppointmentSchema.intakeLink) and patientLinksEnabled; the patient's session
 * says which sections to ask (PublicAppointmentSessionSchema.questionnaireSections).
 *
 * This file imports scheduling.ts and never the other way round.
 */
// ---------------------------------------------------------------------------
// Quick desk details
// ---------------------------------------------------------------------------
/**
 * A phone number as its 10 digits (a leading 1 dropped from 11 digits), or null: the same rule
 * as the chart matcher's dob_and_phone signal (private.scheduling_phone_digits) and what a text
 * message (PA-7) needs.
 */
export function phoneTenDigits(value) {
    const digits = value.replace(/\D/g, "");
    if (digits.length === 10)
        return digits;
    if (digits.length === 11 && digits.startsWith("1"))
        return digits.slice(1);
    return null;
}
/** A UTF-16 surrogate standing alone, which PostgreSQL text cannot hold. */
const LONE_SURROGATE = /\p{Cs}/u;
function deskText(label, required, min, max) {
    return z
        .string()
        .trim()
        .min(min, required)
        .max(max, `${label} must be ${max} characters or fewer`)
        .refine((value) => !value.includes("\u0000") && !LONE_SURROGATE.test(value), `${label} contains a character that cannot be saved`);
}
const WalkInLegalFirstNameSchema = deskText("Legal first name", "Enter the first name", 1, 100);
const WalkInLegalLastNameSchema = deskText("Legal last name", "Enter the last name", 1, 100);
const WalkInDateOfBirthSchema = z.iso
    .date({
    error: (issue) => issue.input === "" ? "Enter the date of birth" : "Enter a real date of birth",
})
    // Year 0000 passes the ISO check but is no date PostgreSQL can hold.
    .refine((value) => !value.startsWith("0000-"), "Enter a real date of birth");
/** An optional mobile number: blank is left out, anything else must have 10 digits. */
const WalkInMobileSchema = z
    .string()
    .trim()
    .max(40, "Mobile number must be 40 characters or fewer")
    .refine((value) => value === "" || phoneTenDigits(value) !== null, "Enter a 10-digit mobile number, or leave it blank")
    .transform((value) => (value === "" ? undefined : value))
    .optional();
/** The visit's triage urgency, as create_walk_in and start_patient_encounter take it. */
export const WalkInUrgencySchema = z.enum(["routine", "soon", "urgent"]);
/**
 * The desk's quick walk-in details. They fit both existing walk-in writers without a backend
 * change: create_walk_in for a new chart (walkInQuickCreateInput, coverage "cash" until staff
 * apply the patient's answers through the intake review), and start_patient_encounter for a
 * chart picked from the duplicate check (walkInQuickStartEncounterInput, which ignores the typed
 * identity: the chart's own values stay, and corrections go through the chart editors).
 */
export const WalkInQuickRegistrationSchema = z
    .object({
    legalFirstName: WalkInLegalFirstNameSchema,
    legalLastName: WalkInLegalLastNameSchema,
    dateOfBirth: WalkInDateOfBirthSchema,
    /** Required; "Prefer not to say" is `unknown`, which the questionnaire may correct later. */
    administrativeSex: z.enum(PatientAdministrativeSexSchema.enum, {
        error: "Choose a sex, or Prefer not to say",
    }),
    /** The chief complaint the visit starts with. */
    reason: deskText("Reason for visit", "Enter the reason for the visit", 2, 500),
    urgency: WalkInUrgencySchema.default("routine"),
    /** Optional mobile number, kept on a new chart for texting the link later (PA-7). */
    phone: WalkInMobileSchema,
})
    .strict();
export function walkInQuickCreateInput(registration) {
    return {
        legalFirstName: registration.legalFirstName,
        legalLastName: registration.legalLastName,
        dateOfBirth: registration.dateOfBirth,
        administrativeSex: registration.administrativeSex,
        ...(registration.phone === undefined ? {} : { phone: registration.phone }),
        chiefComplaint: registration.reason,
        urgency: registration.urgency,
        coverage: { kind: "cash" },
    };
}
/** start_patient_encounter's input for a chart picked from the duplicate check. */
export function walkInQuickStartEncounterInput(registration) {
    return { chiefComplaint: registration.reason, urgency: registration.urgency };
}
// ---------------------------------------------------------------------------
// Duplicate check (PA-6's one chart matcher)
// ---------------------------------------------------------------------------
/**
 * `POST /v1/scheduling/walk-ins/chart-matches`: the probable charts for what the desk typed,
 * before a walk-in chart is made (lead decision H1). The store runs the same matcher as the
 * questionnaire review (private.probable_chart_matches): name and date of birth, and date of
 * birth and mobile number when one was typed. Answered with SchedulingPatientMatchesSchema,
 * basis walk_in, every match linkable and none dismissed. The read is audited; nothing typed is
 * stored or written to the audit row.
 */
export const WalkInChartMatchesRequestSchema = z
    .object({
    legalFirstName: WalkInLegalFirstNameSchema,
    legalLastName: WalkInLegalLastNameSchema,
    dateOfBirth: WalkInDateOfBirthSchema,
    phone: WalkInMobileSchema,
})
    .strict();
/**
 * The duplicate check for the desk's details so far, or null until first name, last name and
 * date of birth are filled in and valid. A mobile number is sent only once it is valid.
 */
export function walkInChartMatchesRequest(values) {
    const phone = WalkInMobileSchema.safeParse(values.phone ?? "");
    const parsed = WalkInChartMatchesRequestSchema.safeParse({
        legalFirstName: values.legalFirstName,
        legalLastName: values.legalLastName,
        dateOfBirth: values.dateOfBirth,
        ...(phone.success && phone.data !== undefined ? { phone: phone.data } : {}),
    });
    return parsed.success ? parsed.data : null;
}
// ---------------------------------------------------------------------------
// Issue or re-issue a visit's questionnaire link
// ---------------------------------------------------------------------------
/**
 * `POST /v1/scheduling/visits/:encounterId/intake-link`, by any of the four scheduling roles.
 * The visit must be on the member's site, open (waiting, roomed, in progress or ready for
 * discharge) and on a chart, and its questionnaire not yet submitted (intake_already_completed);
 * a closed visit is refused (visit_closed), and so is every request while the patient lane is
 * off (patient_links_disabled, 503).
 *
 * One link works at a time. A live link the patient has not opened is revoked and replaced. A
 * live link the patient opened is replaced only when replaceOpenedLinkId names it, after the
 * desk confirmed (lead decision H7); otherwise the request is refused (intake_link_opened) and
 * the desk reads the link from the schedule state to ask.
 */
export const IssueVisitIntakeLinkRequestSchema = z
    .object({
    /** The opened live link the desk confirmed replacing. Authorizes replacing only that link. */
    replaceOpenedLinkId: z.string().uuid().optional(),
})
    .strict();
/**
 * The issued link, returned once. intakeQuestionnaireUrl is the only place the token ever
 * appears: the desk encodes it in the QR code and shows it until the answers arrive, it expires
 * or the code is hidden, and stores, logs and copies it nowhere. The schedule read lists the link
 * without its URL (VisitIntakeLinkSchema).
 */
export const VisitIntakeLinkResponseSchema = z
    .object({
    /** The visit's scheduling row: the walk-in's own row, or the booking the visit came from. */
    schedulingAppointmentId: z.string().uuid(),
    /** walk_in for a walk-in's row (SchedulingStateSchema.walkIns); else the booking's source. */
    source: SchedulingAppointmentSourceSchema,
    version: z.number().int().positive(),
    /**
     * What the link asks. About you only when this visit created the chart (a walk-in
     * registered at the desk today); never for a booking, which already has its details.
     */
    questionnaireSections: PatientIntakeSectionsSchema,
    link: VisitIntakeLinkSchema,
    /** The live link this one replaced and revoked, if there was one. */
    replacedLinkId: z.string().uuid().optional(),
    intakeQuestionnaireUrl: PublicIntakeQuestionnaireUrlSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.link.opened) {
        context.addIssue({
            code: "custom",
            message: "A new link has not been opened",
            path: ["link", "opened"],
        });
    }
    if (value.replacedLinkId === value.link.id) {
        context.addIssue({
            code: "custom",
            message: "A link cannot replace itself",
            path: ["replacedLinkId"],
        });
    }
});
//# sourceMappingURL=scheduling-walk-in.js.map